/*
 * Design: Gradient Noir – Clients page with logos, testimonials, and industries
 */
import { Link } from "wouter";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useApiData } from "@/hooks/useApiData";
import { Star, Quote, Building2, ShoppingBag, Stethoscope, GraduationCap, Home, Utensils, Car, Briefcase } from "lucide-react";
import type { Testimonial, Client } from "@shared/types";
import ClientLogo from "@/components/ClientLogo";

function AnimatedSection({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollAnimation(0.1);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(30px)",
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}


const industries = [
  { icon: Building2, name: "נדל\"ן" },
  { icon: ShoppingBag, name: "מסחר אלקטרוני" },
  { icon: Stethoscope, name: "רפואה ובריאות" },
  { icon: GraduationCap, name: "חינוך והדרכה" },
  { icon: Home, name: "שיפוצים ובנייה" },
  { icon: Utensils, name: "מסעדנות" },
  { icon: Car, name: "רכב" },
  { icon: Briefcase, name: "שירותים מקצועיים" },
];


export default function Clients() {
  const { data: testimonials } = useApiData<Testimonial[]>("/testimonials");
  const { data: clientLogos } = useApiData<Client[]>("/clients");

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 gradient-mesh-strong" />
        <div className="absolute top-20 left-1/3 w-96 h-96 bg-[#66CDAA]/10 rounded-full blur-[120px]" />
        <div className="container relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 font-[Rubik] leading-tight">
              <span className="gradient-text">הלקוחות</span> שלנו
            </h1>
            <p className="text-lg text-white/60 leading-relaxed max-w-2xl">
              גאים לעבוד עם מאות עסקים וחברות מכל הגדלים והתחומים. הנה חלק מהלקוחות שסומכים עלינו.
            </p>
          </div>
        </div>
      </section>

      {/* Client Logos Grid */}
      <section className="py-16 lg:py-24 section-navy">
        <div className="container">
          <AnimatedSection>
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-12 font-[Rubik] text-center">
              חלק מהלקוחות שעובדים איתנו
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {(clientLogos || []).map((client, i) => (
              <AnimatedSection key={client.id} delay={i * 50}>
                <div className="glass-card p-6 flex items-center justify-center h-32 hover:border-[#66CDAA]/20 transition-colors group">
                  {client.svgCode || client.logo ? (
                    <ClientLogo client={client} className={`max-w-full object-contain rounded-xl transition-all duration-300 ${client.svgCode ? "h-16 w-full" : "max-h-20"}`} />
                  ) : (
                    <span className="text-lg font-bold text-white/30 font-[Rubik]">{client.name}</span>
                  )}
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials && testimonials.length > 0 && (
      <section className="py-20 lg:py-28 section-dark relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-l from-transparent via-[#66CDAA]/20 to-transparent" />
        <div className="absolute bottom-1/3 right-0 w-96 h-96 bg-[#66CDAA]/5 rounded-full blur-[120px]" />
        <div className="container relative z-10">
          <AnimatedSection>
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4 font-[Rubik]">
                מה <span className="gradient-text">הלקוחות</span> אומרים
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {testimonials.map((t, i) => (
              <AnimatedSection key={t.id} delay={i * 100}>
                <div className="glass-card glass-card-hover p-8 transition-all duration-300 h-full">
                  <Quote className="w-8 h-8 text-[#66CDAA]/30 mb-4" />
                  <p className="text-white/70 leading-relaxed mb-6">{t.text}</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-white font-[Rubik]">{t.name}</div>
                      <div className="text-sm text-white/40">{t.company}</div>
                    </div>
                    <div className="flex gap-0.5">
                      {Array.from({ length: t.rating }).map((_, j) => (
                        <Star key={j} className="w-4 h-4 fill-[#66CDAA] text-[#66CDAA]" />
                      ))}
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Industries */}
      <section className="py-20 lg:py-28 section-navy relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-l from-transparent via-[#66CDAA]/20 to-transparent" />
        <div className="container">
          <AnimatedSection>
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4 font-[Rubik]">
                תחומים שאנחנו <span className="gradient-text">מתמחים</span> בהם
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {industries.map((ind, i) => (
              <AnimatedSection key={i} delay={i * 80}>
                <div className="glass-card glass-card-hover p-6 text-center transition-all duration-300">
                  <div className="w-12 h-12 rounded-xl bg-[#66CDAA]/10 flex items-center justify-center mx-auto mb-3">
                    <ind.icon className="w-6 h-6 text-[#66CDAA]" />
                  </div>
                  <span className="text-sm font-medium text-white/70">{ind.name}</span>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 gradient-mesh-strong" />
        <div className="container relative z-10 text-center">
          <AnimatedSection>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 font-[Rubik]">
              רוצים להצטרף ללקוחות שלנו?
            </h2>
            <p className="text-lg text-white/60 mb-8 max-w-xl mx-auto">
              דברו איתנו ונראה לכם איך אנחנו יכולים לעזור גם לעסק שלכם.
            </p>
            <Link href="/contact" className="glow-btn inline-block text-lg px-10 py-4">
              דברו איתנו
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
